<template>
    <div class="min-h-screen background-logo flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div class="max-w-md w-full space-y-8">
            <!-- Header -->
            <div class="text-center">
                <h1 class="text-4xl font-bold text-white mb-2 drop-shadow-lg text-shadow">
                    Inscription
                </h1>
                <p class="text-gray-200 drop-shadow-md text-shadow">
                    Créez votre compte Liview
                </p>
            </div>

            <!-- Formulaire -->
            <div class="background-color backdrop-blur-sm rounded-xl shadow-2xl p-8 border border-white/20">
                <form @submit.prevent="handleRegister" class="space-y-6">
                    <!-- Username -->
                    <div>
                        <label for="username" class="block text-sm font-medium text-gray-700 mb-2">
                            Nom d'utilisateur
                        </label>
                        <input 
                            id="username"
                            type="text" 
                            v-model="username" 
                            placeholder="Choisissez un nom d'utilisateur"
                            class="input-background w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-white placeholder-gray-300"
                            :class="{ 'border-red-500': error && (!username || username.length < 3) }"
                            required
                        />
                    </div>

                    <!-- Email -->
                    <div>
                        <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
                            Adresse email
                        </label>
                        <input 
                            id="email"
                            type="email" 
                            v-model="email" 
                            placeholder="Entrez votre adresse email"
                            class="input-background w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-white placeholder-gray-300"
                            :class="{ 'border-red-500': error && (!email || !email.includes('@')) }"
                            required
                        />
                    </div>

                    <!-- Password -->
                    <div>
                        <label for="password" class="block text-sm font-medium text-gray-700 mb-2">
                            Mot de passe
                        </label>
                        <input 
                            id="password"
                            type="password" 
                            v-model="password" 
                            placeholder="Créez un mot de passe"
                            class="input-background w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-white placeholder-gray-300"
                            :class="{ 'border-red-500': error && (!password || password.length < 6) }"
                            required
                        />
                    </div>

                    <!-- Confirm Password -->
                    <div>
                        <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-2">
                            Confirmer le mot de passe
                        </label>
                        <input 
                            id="confirmPassword"
                            type="password" 
                            v-model="confirmPassword" 
                            placeholder="Confirmez votre mot de passe"
                            class="input-background w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-white placeholder-gray-300"
                            :class="{ 'border-red-500': error && password !== confirmPassword }"
                            required
                        />
                    </div>

                    <!-- Error Message -->
                    <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div class="flex">
                            <svg class="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                            </svg>
                            <p class="text-red-600 text-sm">{{ error }}</p>
                        </div>
                    </div>

                    <!-- Submit Button -->
                    <button 
                        type="submit"
                        :disabled="isLoading"
                        class="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                        <svg v-if="isLoading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ isLoading ? 'Inscription...' : 'S\'inscrire' }}
                    </button>

                    <!-- Link to Login -->
                    <div class="text-center">
                        <p class="text-gray-600 text-sm">
                            Déjà un compte ? 
                            <router-link to="/login" class="text-blue-600 hover:text-blue-500 font-medium transition-colors duration-200">
                                Se connecter
                            </router-link>
                        </p>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import * as NAMESPACE_STORE from "../vuexStore/namespaceStore.js";

export default {
    name: "Register",
    data() {
        return {
            username: "",
            email: "",
            password: "",
            confirmPassword: "",
            error: "",
            isLoading: false
        }
    },
    computed: {
        ...mapGetters(NAMESPACE_STORE.USER, {
            isLoggedIn: "isLoggedIn"
        })
    },
    watch: {
        // Rediriger automatiquement si l'utilisateur est connecté
        isLoggedIn(newValue) {
            if (newValue) {
                this.$router.push('/editor');
            }
        }
    },
    methods: {
        ...mapActions(NAMESPACE_STORE.USER, {
            registerAction: "register"
        }),
        handleRegister() {
            // Validation des champs
            if (!this.username || !this.email || !this.password || !this.confirmPassword) {
                this.error = "Veuillez remplir tous les champs";
                return;
            }

            if (this.username.length < 3) {
                this.error = "Le nom d'utilisateur doit contenir au moins 3 caractères";
                return;
            }

            if (this.password.length < 6) {
                this.error = "Le mot de passe doit contenir au moins 6 caractères";
                return;
            }

            if (this.password !== this.confirmPassword) {
                this.error = "Les mots de passe ne correspondent pas";
                return;
            }

            if (!this.email.includes('@') || !this.email.includes('.')) {
                this.error = "Format d'email invalide";
                return;
            }

            this.isLoading = true;
            this.error = "";

            this.registerAction({
                username: this.username,
                email: this.email,
                password: this.password,
            }).then(() => {
                this.$router.push('/editor');
            }).catch(error => {
                this.error = "Erreur lors de l'inscription. Vérifiez vos informations.";
                console.error('Erreur register:', error);
            }).finally(() => {
                this.isLoading = false;
            });
        }
    }
}
</script>

<style scoped>
.background-logo {
    background-image: url('/assets/images/logo.png');
    background-repeat: no-repeat;
    background-position: 50% 30%;
    background-size: contain;
}
.text-shadow {
    text-shadow: 0px 0px 2px rgb(0, 0, 0);
}
.background-color {
    background-color: #ffffffc9;
}
.input-background {
    background-color: #2b2a33;
}
</style>